import { useCallback, useEffect, useState } from 'react';
import type { EventEffect, GalacticEventView } from '@galaxis/shared';
import { fetchGalacticEvents, decideGalacticEvent } from '../../api/social.ts';
import { useGameStore } from '../../store/game-store.ts';

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));
function effectText(e: EventEffect): string {
  const parts = [e.metal && `${signed(e.metal)} fém`, e.energy && `${signed(e.energy)} energia`, e.credits && `${signed(e.credits)} kredit`].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'nincs hatás';
}

function remaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'lejárt';
  const m = Math.ceil(ms / 60_000);
  return m >= 120 ? `${Math.round(m / 60)} óra` : `${m} perc`;
}

export function EventsPanel({ onChanged }: { onChanged: (openDecisions: number) => void }) {
  const [events, setEvents] = useState<GalacticEventView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const pushToast = useGameStore(s => s.pushToast);

  const load = useCallback(() => {
    fetchGalacticEvents().then(r => { setEvents(r.events); onChanged(r.events.filter(e => e.canDecide && !e.chosen).length); })
      .catch(e => pushToast(e instanceof Error ? e.message : 'Hiba', 'error'));
  }, [onChanged, pushToast]);
  useEffect(load, [load]);

  const decide = async (id: string, key: string) => {
    setBusy(true);
    try { await decideGalacticEvent(id, key); pushToast('Döntés rögzítve'); load(); }
    catch (e) { pushToast(e instanceof Error ? e.message : 'A döntés nem rögzíthető', 'error'); }
    finally { setBusy(false); }
  };

  if (!events) return <p className="text-sm text-slate-500">Események betöltése…</p>;
  if (events.length === 0) return <p className="text-sm text-slate-500" data-testid="no-events">Jelenleg nincs galaktikus esemény. Az újak a szerver szabályai szerint, időnként érkeznek.</p>;

  return (
    <ul className="space-y-4" data-testid="events-list">
      {events.map(ev => (
        <li key={ev.id} className="sci-fi-panel rounded-xl p-4" data-testid={`event-${ev.kind}`} data-status={ev.status}>
          <div className="flex flex-wrap justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-100">{ev.title}</h2>
            <span className="text-xs text-slate-400">
              {ev.status === 'resolved' ? 'lezárult' : ev.canDecide ? `döntési idő: ${remaining(ev.decisionEndsAt)}` : 'tájékoztató'}
            </span>
          </div>
          <p className="text-sm text-slate-300 mt-1">{ev.intro}</p>
          {ev.options.length > 0 && (
            <ul className="mt-3 grid md:grid-cols-3 gap-2">
              {ev.options.map(o => {
                const picked = ev.chosen === o.key;
                return (
                  <li key={o.key} className={`rounded-lg border p-3 ${picked ? 'border-accent/70 bg-accent/10' : 'border-slate-700'}`}>
                    <div className="text-sm font-medium text-slate-100">{o.label}</div>
                    <p className="text-xs text-slate-400 mt-1">{o.description}</p>
                    <p className="text-[11px] font-mono text-slate-500 mt-1">{effectText(o.effect)}</p>
                    {ev.canDecide && (
                      <button type="button" className="btn-primary w-full text-xs mt-2 min-h-[44px]" disabled={busy || picked} onClick={() => void decide(ev.id, o.key)}>
                        {picked ? '✓ Választva' : 'Ezt választom'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {ev.status === 'resolved' && ev.applied && (
            <p className="text-xs text-emerald-400 mt-3" data-testid="event-applied">Következmény alkalmazva: {effectText(ev.applied)}</p>
          )}
          {ev.canDecide && !ev.chosen && <p className="text-xs text-amber-300 mt-3">Ha nem döntesz, az alapértelmezett lehetőség érvényesül a határidő után.</p>}
        </li>
      ))}
    </ul>
  );
}
